'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
type SessionTab = 'upcoming' | 'past' | 'cancelled';

interface ClientSession {
  id: string;
  consultantId: string;
  consultantName: string;
  consultantInitials: string;
  consultantTitle: string;
  consultantCategory: string;
  service: string;
  date: string;
  time: string;
  duration: string;
  amountUsd: number;
  paymentStatus: 'paid' | 'pending' | 'refunded' | 'failed';
  bookingStatus: 'upcoming' | 'completed' | 'cancelled' | 'pending';
  meetingLink?: string;
  notes?: string;
  hasReview: boolean;
  bookedAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatRelativeDate(dateStr: string): string {
  if (!dateStr) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T00:00:00');
  const diff = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 0 && diff <= 7) return `In ${diff} days`;
  if (diff < 0 && diff >= -7) return `${Math.abs(diff)} days ago`;
  return formatDate(dateStr);
}

function getStatusBadge(status: ClientSession['bookingStatus']) {
  const map: Record<string, { label: string; cls: string; icon: string }> = {
    upcoming: { label: 'Upcoming', cls: 'bg-primary/10 text-primary', icon: 'ClockIcon' },
    pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700', icon: 'ExclamationCircleIcon' },
    completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700', icon: 'CheckCircleIcon' },
    cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-700', icon: 'XCircleIcon' },
  };
  return map[status] ?? { label: status, cls: 'bg-muted text-muted-foreground', icon: 'QuestionMarkCircleIcon' };
}

function getCategoryColor(category: string): string {
  const map: Record<string, string> = {
    'Islamic Finance': 'bg-teal-100 text-teal-700',
    'Business Strategy': 'bg-violet-100 text-violet-700',
    'Tech & Digital': 'bg-blue-100 text-blue-700',
    'Halal Legal': 'bg-orange-100 text-orange-700',
    'Halal Marketing': 'bg-pink-100 text-pink-700',
  };
  return map[category] ?? 'bg-muted text-muted-foreground';
}

// ─── Reschedule Modal ─────────────────────────────────────────────────────────
function RescheduleModal({
  session,
  onClose,
  onConfirm,
}: {
  session: ClientSession;
  onClose: () => void;
  onConfirm: (id: string, date: string, time: string) => void;
}) {
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md border border-border">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div>
            <h3 className="text-base font-600 text-foreground">Reschedule Session</h3>
            <p className="text-xs text-muted-foreground mt-0.5">with {session.consultantName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-500 text-muted-foreground mb-1.5">New Date</label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="block text-xs font-500 text-muted-foreground mb-1.5">New Time</label>
            <div className="grid grid-cols-3 gap-2">
              {timeSlots.map((slot) => (
                <button
                  key={slot}
                  onClick={() => setNewTime(slot)}
                  className={`px-2 py-2 rounded-lg text-xs font-500 border transition-all ${
                    newTime === slot
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-500 text-muted-foreground hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (newDate && newTime) onConfirm(session.id, newDate, newTime); }}
            disabled={!newDate || !newTime}
            className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Reschedule
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cancel Modal ─────────────────────────────────────────────────────────────
function CancelModal({
  session,
  onClose,
  onConfirm,
}: {
  session: ClientSession;
  onClose: () => void;
  onConfirm: (id: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm border border-border">
        <div className="px-6 py-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Icon name="ExclamationTriangleIcon" size={24} className="text-red-600" />
          </div>
          <h3 className="text-base font-600 text-foreground mb-1">Cancel Session?</h3>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to cancel your session with{' '}
            <span className="font-500 text-foreground">{session.consultantName}</span>?
          </p>
          {session.paymentStatus === 'paid' && (
            <div className="mt-3 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700">
                A refund of <span className="font-600">${session.amountUsd}</span> will be processed within 5–7 business days.
              </p>
            </div>
          )}
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-500 text-muted-foreground hover:bg-muted transition-colors"
          >
            Keep Session
          </button>
          <button
            onClick={() => onConfirm(session.id)}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-600 hover:bg-red-700 transition-colors"
          >
            Yes, Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Session Card ─────────────────────────────────────────────────────────────
function SessionCard({
  session,
  onReschedule,
  onCancel,
}: {
  session: ClientSession;
  onReschedule: (s: ClientSession) => void;
  onCancel: (s: ClientSession) => void;
}) {
  const statusBadge = getStatusBadge(session.bookingStatus);
  const categoryColor = getCategoryColor(session.consultantCategory);
  const canAct = session.bookingStatus === 'upcoming' || session.bookingStatus === 'pending';
  const isPast = session.bookingStatus === 'completed';
  const relDate = formatRelativeDate(session.date);

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden hover:shadow-card transition-shadow duration-200">
      {/* Header */}
      <div className="px-5 py-4 flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl gradient-teal flex items-center justify-center text-white font-600 text-sm flex-shrink-0">
          {session.consultantInitials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <p className="text-sm font-600 text-foreground">{session.consultantName}</p>
              <p className="text-xs text-muted-foreground truncate">{session.consultantTitle}</p>
            </div>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-500 flex-shrink-0 ${statusBadge.cls}`}>
              <Icon name={statusBadge.icon as Parameters<typeof Icon>[0]['name']} size={12} />
              {statusBadge.label}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-500 ${categoryColor}`}>
              {session.consultantCategory}
            </span>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="px-5 pb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="flex items-center gap-2">
          <Icon name="CalendarDaysIcon" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Date</p>
            <p className="text-xs font-500 text-foreground">{relDate}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="ClockIcon" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Time</p>
            <p className="text-xs font-500 text-foreground">{session.time || '—'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="BriefcaseIcon" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Duration</p>
            <p className="text-xs font-500 text-foreground">{session.duration}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="CurrencyDollarIcon" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Amount</p>
            <p className="text-xs font-600 text-foreground">${session.amountUsd}</p>
          </div>
        </div>
      </div>

      {/* Service */}
      <div className="px-5 pb-3">
        <p className="text-xs text-muted-foreground">
          <span className="font-500 text-foreground">Service: </span>
          {session.service}
        </p>
      </div>

      {/* Actions */}
      <div className="px-5 py-3 border-t border-border flex items-center gap-2 flex-wrap">
        {session.meetingLink && canAct && (
          <a
            href={session.meetingLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-500 hover:bg-primary/90 transition-colors"
          >
            <Icon name="VideoCameraIcon" size={13} />
            Join Session
          </a>
        )}
        {canAct && (
          <button
            onClick={() => onReschedule(session)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-500 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Icon name="ArrowPathIcon" size={13} />
            Reschedule
          </button>
        )}
        {canAct && (
          <button
            onClick={() => onCancel(session)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-xs font-500 text-red-600 hover:bg-red-50 transition-colors"
          >
            <Icon name="XMarkIcon" size={13} />
            Cancel
          </button>
        )}
        {isPast && !session.hasReview && (
          <Link
            href={`/session-reviews?booking=${session.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-xs font-500 text-amber-700 hover:bg-amber-100 transition-colors"
          >
            <Icon name="StarIcon" size={13} />
            Leave Review
          </Link>
        )}
        {isPast && session.hasReview && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-xs font-500 text-emerald-700">
            <Icon name="CheckCircleIcon" size={13} />
            Reviewed
          </span>
        )}
        <Link
          href={`/browse-consultants`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-500 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors ml-auto"
        >
          <Icon name="UserCircleIcon" size={13} />
          View Consultant
        </Link>
      </div>
    </div>
  );
}

// ─── Summary Cards ────────────────────────────────────────────────────────────
function SummaryCards({ sessions }: { sessions: ClientSession[] }) {
  const upcoming = sessions.filter(s => s.bookingStatus === 'upcoming' || s.bookingStatus === 'pending').length;
  const completed = sessions.filter(s => s.bookingStatus === 'completed').length;
  const cancelled = sessions.filter(s => s.bookingStatus === 'cancelled').length;
  const totalSpent = sessions.filter(s => s.paymentStatus === 'paid').reduce((acc, s) => acc + s.amountUsd, 0);

  const cards = [
    { label: 'Upcoming', value: upcoming, icon: 'CalendarDaysIcon', color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Completed', value: completed, icon: 'CheckCircleIcon', color: 'text-emerald-600', bg: 'bg-emerald-100' },
    { label: 'Cancelled', value: cancelled, icon: 'XCircleIcon', color: 'text-red-500', bg: 'bg-red-100' },
    { label: 'Total Spent', value: `$${totalSpent.toFixed(0)}`, icon: 'CurrencyDollarIcon', color: 'text-violet-600', bg: 'bg-violet-100' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map((card) => (
        <div key={card.label} className="bg-card border border-border rounded-2xl px-5 py-4 flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon name={card.icon as Parameters<typeof Icon>[0]['name']} size={20} className={card.color} />
          </div>
          <div>
            <p className="text-xl font-700 text-foreground">{card.value}</p>
            <p className="text-xs text-muted-foreground">{card.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function MySessionsPage() {
  const supabase = createClient();
  const [activeTab, setActiveTab] = useState<SessionTab>('upcoming');
  const [sessions, setSessions] = useState<ClientSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<ClientSession | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ClientSession | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  function showToast(msg: string, type: 'success' | 'error') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  useEffect(() => {
    async function fetchSessions() {
      setLoading(true);
      setError(null);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setSessions([]);
          setLoading(false);
          return;
        }

        const { data, error: fetchError } = await supabase
          .from('consultation_bookings')
          .select(`
            id,
            service_id,
            service_title,
            provider_name,
            booking_date,
            start_time,
            end_time,
            duration_minutes,
            status,
            payment_status,
            amount_usd,
            meeting_link,
            notes,
            created_at,
            consultant_id
          `)
          .eq('client_user_id', user.id)
          .order('booking_date', { ascending: false });

        if (fetchError) {
          setError(fetchError.message);
          setLoading(false);
          return;
        }

        // Fetch consultant profiles
        const consultantIds = [...new Set((data ?? []).map((b: any) => b.consultant_id).filter(Boolean))];
        let profileMap: Record<string, { display_name: string; professional_title: string; category: string }> = {};

        if (consultantIds.length > 0) {
          const { data: profiles } = await supabase
            .from('consultant_profiles')
            .select('id, display_name, professional_title, category')
            .in('id', consultantIds);

          (profiles ?? []).forEach((p: any) => {
            profileMap[p.id] = {
              display_name: p.display_name,
              professional_title: p.professional_title,
              category: p.category,
            };
          });
        }

        // Fetch reviews given by this user
        const bookingIds = (data ?? []).map((b: any) => b.id);
        let reviewedBookingIds = new Set<string>();
        if (bookingIds.length > 0) {
          const { data: reviews } = await supabase
            .from('session_reviews')
            .select('booking_id')
            .eq('reviewer_id', user.id)
            .in('booking_id', bookingIds);
          (reviews ?? []).forEach((r: any) => {
            if (r.booking_id) reviewedBookingIds.add(r.booking_id);
          });
        }

        const mapped: ClientSession[] = (data ?? []).map((row: any) => {
          const profile = profileMap[row.consultant_id ?? ''];
          const consultantName = profile?.display_name ?? row.provider_name ?? 'Unknown Consultant';
          const nameWords = consultantName.split(' ');
          const initials = nameWords.slice(0, 2).map((w: string) => w[0] ?? '').join('').toUpperCase();

          const statusMap: Record<string, ClientSession['bookingStatus']> = {
            pending: 'pending',
            confirmed: 'upcoming',
            completed: 'completed',
            cancelled: 'cancelled',
          };
          const bookingStatus = statusMap[row.status] ?? 'pending';

          const paymentStatusMap: Record<string, ClientSession['paymentStatus']> = {
            paid: 'paid',
            unpaid: 'pending',
            refunded: 'refunded',
            failed: 'failed',
          };
          const paymentStatus = paymentStatusMap[row.payment_status ?? 'unpaid'] ?? 'pending';

          const startTime = row.start_time ? row.start_time.slice(0, 5) : '';
          const [h] = startTime.split(':').map(Number);
          const period = !isNaN(h) ? (h < 12 ? 'AM' : 'PM') : '';
          const displayTime = startTime ? `${startTime} ${period}` : '';

          const mins = row.duration_minutes ?? 60;
          const durationLabel = mins < 60 ? `${mins} min` : mins === 60 ? '1 hour' : `${mins / 60} hours`;

          return {
            id: row.id,
            consultantId: row.consultant_id ?? '',
            consultantName,
            consultantInitials: initials,
            consultantTitle: profile?.professional_title ?? '',
            consultantCategory: profile?.category ?? 'Consulting',
            service: row.service_title ?? '',
            date: row.booking_date ?? '',
            time: displayTime,
            duration: durationLabel,
            amountUsd: parseFloat(row.amount_usd ?? 0),
            paymentStatus,
            bookingStatus,
            meetingLink: row.meeting_link ?? undefined,
            notes: row.notes ?? undefined,
            hasReview: reviewedBookingIds.has(row.id),
            bookedAt: row.created_at ?? '',
          };
        });

        setSessions(mapped);
      } catch (err: any) {
        setError(err?.message ?? 'Failed to load sessions');
      } finally {
        setLoading(false);
      }
    }

    fetchSessions();
  }, []);

  async function handleReschedule(id: string, date: string, time: string) {
    try {
      const { error: updateError } = await supabase
        .from('consultation_bookings')
        .update({ booking_date: date, start_time: time, status: 'confirmed' })
        .eq('id', id);

      if (updateError) throw updateError;

      setSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, date, time, bookingStatus: 'upcoming' } : s))
      );
      showToast('Session rescheduled successfully.', 'success');
    } catch (err: any) {
      showToast(err?.message ?? 'Failed to reschedule.', 'error');
    } finally {
      setRescheduleTarget(null);
    }
  }

  async function handleCancel(id: string) {
    try {
      const { error: updateError } = await supabase
        .from('consultation_bookings')
        .update({ status: 'cancelled' })
        .eq('id', id);

      if (updateError) throw updateError;

      setSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, bookingStatus: 'cancelled' } : s))
      );
      showToast('Session cancelled.', 'success');
    } catch (err: any) {
      showToast(err?.message ?? 'Failed to cancel.', 'error');
    } finally {
      setCancelTarget(null);
    }
  }

  const tabs: { key: SessionTab; label: string; icon: string }[] = [
    { key: 'upcoming', label: 'Upcoming', icon: 'ClockIcon' },
    { key: 'past', label: 'Past', icon: 'CheckCircleIcon' },
    { key: 'cancelled', label: 'Cancelled', icon: 'XCircleIcon' },
  ];

  const filtered = sessions.filter((s) => {
    if (activeTab === 'upcoming') return s.bookingStatus === 'upcoming' || s.bookingStatus === 'pending';
    if (activeTab === 'past') return s.bookingStatus === 'completed';
    if (activeTab === 'cancelled') return s.bookingStatus === 'cancelled';
    return true;
  });

  return (
    <AppLayout activeRoute="/my-sessions">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-5 right-5 z-[100] px-4 py-3 rounded-xl shadow-lg text-sm font-500 flex items-center gap-2 transition-all ${
            toast.type === 'success' ?'bg-emerald-600 text-white' :'bg-red-600 text-white'
          }`}
        >
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={16} />
          {toast.msg}
        </div>
      )}

      {/* Modals */}
      {rescheduleTarget && (
        <RescheduleModal
          session={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onConfirm={handleReschedule}
        />
      )}
      {cancelTarget && (
        <CancelModal
          session={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleCancel}
        />
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-700 text-foreground">My Sessions</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Track your past and upcoming consultations</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href="/browse-consultants"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-500 hover:bg-primary/90 transition-colors"
          >
            <Icon name="MagnifyingGlassCircleIcon" size={16} />
            Find Consultants
          </Link>
          <Link
            href="/book-session"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-500 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Icon name="PlusCircleIcon" size={16} />
            Book Session
          </Link>
        </div>
      </div>

      {/* Summary Cards */}
      {!loading && sessions.length > 0 && <SummaryCards sessions={sessions} />}

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-muted/50 rounded-xl p-1 mb-6 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-500 transition-all duration-150 ${
              activeTab === tab.key
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={15} />
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-600 ${
              activeTab === tab.key ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
            }`}>
              {sessions.filter((s) => {
                if (tab.key === 'upcoming') return s.bookingStatus === 'upcoming' || s.bookingStatus === 'pending';
                if (tab.key === 'past') return s.bookingStatus === 'completed';
                if (tab.key === 'cancelled') return s.bookingStatus === 'cancelled';
                return false;
              }).length}
            </span>
          </button>
        ))}
      </div>

      {/* Quick Links Banner (past tab) */}
      {activeTab === 'past' && filtered.some(s => !s.hasReview) && (
        <div className="mb-5 px-5 py-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-center gap-3 flex-wrap">
          <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Icon name="StarIcon" size={18} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-600 text-amber-800">You have sessions awaiting your review</p>
            <p className="text-xs text-amber-600 mt-0.5">Share your experience to help other clients find the right consultant.</p>
          </div>
          <Link
            href="/session-reviews"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 text-white text-xs font-500 hover:bg-amber-700 transition-colors flex-shrink-0"
          >
            <Icon name="StarIcon" size={13} />
            Write Reviews
          </Link>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card border border-border rounded-2xl p-5 animate-pulse">
              <div className="flex gap-4">
                <div className="w-11 h-11 rounded-xl bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/3" />
                  <div className="h-3 bg-muted rounded w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Icon name="ExclamationCircleIcon" size={28} className="text-red-500" />
          </div>
          <p className="text-base font-600 text-foreground mb-1">Failed to load sessions</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="CalendarDaysIcon" size={28} className="text-muted-foreground" />
          </div>
          <p className="text-base font-600 text-foreground mb-1">
            {activeTab === 'upcoming' ? 'No upcoming sessions' : activeTab === 'past' ? 'No past sessions' : 'No cancelled sessions'}
          </p>
          <p className="text-sm text-muted-foreground mb-5">
            {activeTab === 'upcoming' ? 'Book a session with a consultant to get started.' : 'Your session history will appear here.'}
          </p>
          {activeTab === 'upcoming' && (
            <Link
              href="/book-session"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-500 hover:bg-primary/90 transition-colors"
            >
              <Icon name="CalendarIcon" size={16} />
              Book a Session
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              onReschedule={setRescheduleTarget}
              onCancel={setCancelTarget}
            />
          ))}
        </div>
      )}
    </AppLayout>
  );
}
